import { useCallback, useEffect, useMemo, useState } from 'react';

import { reloadPage } from '../utils/platform';

interface IBeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let cachedDeferredPrompt: IBeforeInstallPromptEvent | null = null;

async function refreshServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map(async (registration) => {
      await registration.update();
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
    }),
  );
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true
  );
}

function detectIosBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  const isAppleMobile = /iPad|iPhone|iPod/.test(userAgent);
  const isIpadOsDesktopMode =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isAppleMobile || isIpadOsDesktopMode;
}

function detectAndroidBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  return /Android/i.test(userAgent);
}

export function usePwaInstall() {
  const isIosBrowser = detectIosBrowser();
  const isAndroidBrowser = detectAndroidBrowser();
  const [isPwaInstalled, setIsPwaInstalled] = useState<boolean>(() =>
    detectStandalone(),
  );
  const [deferredPrompt, setDeferredPrompt] =
    useState<IBeforeInstallPromptEvent | null>(cachedDeferredPrompt);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as IBeforeInstallPromptEvent;
      cachedDeferredPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
    };

    const onAppInstalled = () => {
      setIsPwaInstalled(true);
      cachedDeferredPrompt = null;
      setDeferredPrompt(null);
    };

    const media = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => {
      setIsPwaInstalled(detectStandalone());
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    media.addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      media.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const canInstallPwa = useMemo(
    () => !isPwaInstalled && Boolean(deferredPrompt),
    [deferredPrompt, isPwaInstalled],
  );

  const promptInstallPwa = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    cachedDeferredPrompt = null;
    setDeferredPrompt(null);
    return choice.outcome === 'accepted';
  }, [deferredPrompt]);

  const reloadPwa = useCallback(() => {
    void refreshServiceWorkers()
      .catch(() => undefined)
      .finally(() => {
        setTimeout(() => {
          reloadPage();
        }, 350);
      });
  }, []);

  return {
    isPwaInstalled,
    isIosBrowser,
    isAndroidBrowser,
    isMobileBrowser: isIosBrowser || isAndroidBrowser,
    canInstallPwa,
    promptInstallPwa,
    reloadPwa,
  };
}
